import { Component, ElementRef, OnInit, OnDestroy, ViewChild, HostListener, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { PaxOrderService } from '../../services/pax-order.service';
import { NavigationEnd, Router } from '@angular/router';
import { CheckUserRolesService } from '../../services/check-user-roles.service';
import { Observable, Subject, filter, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MultiSelectDropdownComponent } from '../multi-select-dropdown/multi-select-dropdown.component';
import { FadingMessagePopupComponent } from '../fading-message-popup/fading-message-popup.component';

const GROUP_ACCOUNT_SENDER_TENANT_KEYS: string[] = [
  "GLS_AT", "GLS_BE", "GLS_CZ", "GLS_DE", "GLS_DK", "GLS_ES", "GLS_FI", "GLS_FR",
  "GLS_HR", "GLS_HU", "GLS_IE", "GLS_IT", "GLS_LU", "GLS_NL", "GLS_PL", "GLS_PT",
  "GLS_RO", "GLS_RS", "GLS_SI", "GLS_SK", "PARTNER_ACSGR", "PARTNER_INTERLGBG",
  "PARTNER_ITELLAEE", "PARTNER_ITELLALT", "PARTNER_ITELLALV", "PARTNER_MNGTR",
  "PARTNER_MTPOST", "PARTNER_PFW", "PARTNER_POSTNO", "PARTNER_SCHENKERSE",
  "PARTNER_SWISS"
];

const MAX_CLIPBOARD_RECORDS: number = 500;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MultiSelectDropdownComponent,
    FadingMessagePopupComponent
  ],
  selector: 'app-my-pickups-view',
  templateUrl: './my-pickups-view.component.html',
  styleUrls: ['./my-pickups-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PaxOrderService, DatePipe]
})
export class MyPickupsViewComponent implements OnInit, OnDestroy {
  subscription: any;
  accessToken$: Observable<unknown>;
  private destroy$ = new Subject<void>();

  showModalPrintChecked = signal<boolean>(false);

  isChecked = signal<boolean>(false);
  checkAll = signal<boolean>(false);
  checkAllOriginallySelected: boolean = false; // Track if check-all was originally selected for infinite scrolling
  checkedOrdersList: any = [];
  checkedOrdersListWithDetails: any = [];

  // Reference of check-all checkbox
  @ViewChild('checkAllInput') checkAllRef: ElementRef;

  queryParameters: any = {};

  parcelNumber = signal<string>('');
  partnerParcelNumber = signal<string>('');
  tenantKey: string = null;
  pickUpDate: string = '';
  startDate: any | Date = '';
  endDate: any | Date = '';
  requesterName = signal<string>('');
  requesterLocation = signal<string>('');
  pickupLocation = signal<string>('');
  deliveryLocation = signal<string>('');
  orderStatus = signal<string>('NEW');

  pickUpName: string = '';
  pickUpStreet: string = '';
  pickUpHouseNumber: string = '';
  pickUpZipCode: string = '';
  pickUpCity: string = '';
  pickUpCountry: string = '';

  deliveryName: string = '';
  deliveryStreet: string = '';
  deliveryHouseNumber: string = '';
  deliveryZipCode: string = '';
  deliveryCity: string = '';
  deliveryCountry: string = '';

  allowedLocations: string[] = [];

  checkedParcels: { [orderKey: string]: Set<string> } = {};

  requesterCountry: string = '';
  requesterReference = signal<string>('');
  selectedRequestedPickupDateGt = signal<Date | string>('');
  selectedRequestedPickupDateLte = signal<Date | string>('');

  // Computed signals for API date parameters - auto-derived from selected dates
  requestedPickupDateLte = computed(() => {
    if (!this.selectedRequestedPickupDateLte()) return '';
    const endDate = this.parseAndValidateDate(this.selectedRequestedPickupDateLte());
    if (!endDate) return '';
    return this.datePipe.transform(endDate, 'yyyy-MM-dd') || '';
  });

  requestedPickupDateGt = computed(() => {
    if (!this.selectedRequestedPickupDateGt()) return '';
    const startDate = this.parseAndValidateDate(this.selectedRequestedPickupDateGt());
    if (!startDate) return '';
    return this.getPreviousDate(startDate);
  });
  datesLoadedFromLocalStorage: boolean = false;
  private processingDateBlur: boolean = false;
  limit: number = 30;
  offset: number = 0;
  ordersList = signal<any[]>([]);

  // Computed signal for adjusted filtered orders list - auto-transforms ordersList
  adjustedFilteredOrdersList = computed(() => {
    const newList: any[] = [];
    for (let order of this.ordersList()) {
      const parcelDetailsExist = order.parcelDetails && order.parcelDetails.length > 0;
      newList.push({
        paxOrderKey: order.paxOrderKey,
        parcelNumber: parcelDetailsExist ? order.parcelDetails[0]['parcelKey'] : '',
        partnerParcelNumber: order['partnerParcelNumber'],
        pickUpDate: order.requestedPickupDate,
        requestedPickupDateGt: order.requestedPickupDateGt,
        requestedPickupDateLte: order.requestedPickupDateLte,
        requesterName: order.requester['name'],
        requesterReference: order['requesterReference'],
        requesterLocation: order['requesterLocation'],
        pickupLocation: order['pickupLocation'],
        deliveryLocation: order['deliveryLocation'],
        orderStatus: order['operationalOrderStatus'],

        pickUpName: order.sender['name'],
        pickUpStreet: order.sender['address'].street,
        pickUpHouseNumber: order.sender['address'].houseNumber,
        pickUpZipCode: order.sender['address'].zipCode,
        pickUpCity: order.sender['address'].city,
        pickUpCountry: order.sender['address'].countryCode,
        deliveryName: order.recipient['name'],
        deliveryStreet: order.recipient['address'].street,
        deliveryHouseNumber: order.recipient['address'].houseNumber,
        deliveryZipCode: order.recipient['address'].zipCode,
        deliveryCity: order.recipient['address'].city,
        deliveryCountry: order.recipient['address'].countryCode,
        parcelDetails: parcelDetailsExist ? order.parcelDetails : [],
        checked: !!order.checked
      });
    }
    return newList;
  });

  adjustedOrder: any = {};
  totalFoundItems = signal<number>(0);
  totalFoundParcels = signal<number>(0);
  dataNotLoaded: boolean = false;
  timerForTyping: any = false;

  isResizing: boolean = false;
  startingX: number = 0;
  startingWidth: number = 0;
  resizingMouseMove: any;
  resizingMouseUp: any;

  currentPath: string = '';

  faultyOrder: string[] = [];

  orderStatusList: string[] = ['NEW', 'IN_PROGRESS', 'PICKED_UP', 'COMPLETE', 'CANCELLED'];
  selectedStatuses = signal<string[]>(['NEW']);

  // Computed signals for formatted dates
  formattedPickupDateGt = computed(() => {
    const date = this.selectedRequestedPickupDateGt();
    if (!date) return null;
    if (date instanceof Date) {
      return this.datePipe.transform(date, 'dd/MM/yyyy');
    }
    try {
      const parsedDate = this.parseAndValidateDate(date);
      return parsedDate ? this.datePipe.transform(parsedDate, 'dd/MM/yyyy') : null;
    } catch (error) {
      console.warn('Error formatting pickup date gt:', error);
      return null;
    }
  });

  formattedPickupDateLte = computed(() => {
    const date = this.selectedRequestedPickupDateLte();
    if (!date) return null;
    if (date instanceof Date) {
      return this.datePipe.transform(date, 'dd/MM/yyyy');
    }
    try {
      const parsedDate = this.parseAndValidateDate(date);
      return parsedDate ? this.datePipe.transform(parsedDate, 'dd/MM/yyyy') : null;
    } catch (error) {
      console.warn('Error formatting pickup date lte:', error);
      return null;
    }
  });

  // Computed signal for filter chips - auto-generates from filter signals
  filterChips = computed(() => {
    const chips: { key: string, value: string }[] = [];

    const parametersToCheck = [
      { key: 'Parcel Number', value: this.parcelNumber() },
      { key: 'Partner Parcel Number', value: this.partnerParcelNumber() },
      { key: 'Pick-up Date - Start', value: this.formattedPickupDateGt() },
      { key: 'Pick-up Date - End', value: this.formattedPickupDateLte() },
      { key: 'Pick-up Location', value: this.pickupLocation() },
      { key: 'Requester Name', value: this.requesterName() },
      { key: 'Requester Reference', value: this.requesterReference() },
      { key: 'Requester Location', value: this.requesterLocation() },
      { key: 'Delivery Location', value: this.deliveryLocation() },
    ];

    // Add individual status chips
    this.selectedStatuses().forEach(status => {
      chips.push({ key: 'Order Status', value: status });
    });

    parametersToCheck.forEach(param => {
      if (param.value && param.value !== '') {
        chips.push({ key: param.key, value: param.value });
      }
    });

    return chips;
  });

  ordersWithCompleteStatus: any = [];
  ordersProperForPrintList;
  ordersProperForPrintSelectedList: any = [];
  isPrintOptionsAllowed = signal<boolean>(false);
  isGroupAdmin: boolean = false;
  deactivatePrintButtonInModalBox = signal<boolean>(false);
  authorizationFailed = signal<boolean>(false);

  private lastBulkPrintContext: {
    bulkPostData: any;
    excludedKeys: string[];
    partialParcelOrderKeys: string[];
    partialParcelKeys: string[];
    estimatedSelectedCount: number;
  } | null = null;

  expandedRows = signal<{ [key: number]: boolean }>({});

  sortingList = signal<string[]>(["-pickupdate"]);

  // Computed signals for sorting flags - auto-derived from sortingList
  isParcelNumberDescendingSortingActive = computed(() => this.sortingList().includes('-parcelnumber'));
  isParcelNumberAscendingSortingActive = computed(() => this.sortingList().includes('parcelnumber'));
  isPickupDateDescendingSortingActive = computed(() => this.sortingList().includes('-pickupdate'));
  isPickupDateAscendingSortingActive = computed(() => this.sortingList().includes('pickupdate'));
  isRequesterLocationDescendingSortingActive = computed(() => this.sortingList().includes('-requesterlocation'));
  isRequesterLocationAscendingSortingActive = computed(() => this.sortingList().includes('requesterlocation'));
  isPickupLocationDescendingSortingActive = computed(() => this.sortingList().includes('-pickuplocation'));
  isPickupLocationAscendingSortingActive = computed(() => this.sortingList().includes('pickuplocation'));
  isDeliveryLocationDescendingSortingActive = computed(() => this.sortingList().includes('-deliverylocation'));
  isDeliveryLocationAscendingSortingActive = computed(() => this.sortingList().includes('deliverylocation'));

  // Computed signal for active sortings - auto-generates from sortingList
  activeSortings = computed(() => {
    const sortings: { key: string, order: 'asc' | 'desc' }[] = [];
    this.sortingList().forEach(sort => {
      const isDescending = sort.startsWith('-');
      const key = isDescending ? sort.substring(1) : sort;
      sortings.push({ key, order: isDescending ? 'desc' : 'asc' });
    });
    return sortings;
  });
  selectedFormat: string = 'pdf';

  isTableLoadingMore = signal<boolean>(false);
  allTableDataLoaded = signal<boolean>(false);
  isDropdownOpen = signal<boolean>(false);

  // Copy to clipboard fading message variables
  copyMessage = signal<string>('');
  copyMessageType = signal<'success' | 'error'>('success');
  copyDisplayDuration: number = 3000;
  copyFadeDuration: number = 1000;
  copyMessageTrigger = signal<number>(0); // Add trigger to force popup refresh

  // Copy loading spinner variables
  showCopySpinner = signal<boolean>(false);
  copySpinnerDuration: number = 3000;
  shouldLimitClipboardCopy = signal<boolean>(false); // Track if copy should be limited to 500 records

  trackByPaxOrder(index: number, item: any): string {
    return item?.paxOrderKey ?? String(index);
  }

  trackByParcelKey(index: number, parcel: any): string {
    return parcel?.parcelKey ?? String(index);
  }

  private lastRequestId = 0;

  constructor(private paxOrderService: PaxOrderService,
    private router: Router,
    private checkUserRolesService: CheckUserRolesService,
    private datePipe: DatePipe) {
  }

  ngOnInit() {
    this.loadCurrentOffsetFromLocalStorage();
    this.loadFiltersFromLocalStorage();
    this.loadSortingChipsFromLocalStorage();
    const pickupDateRemoved = localStorage.getItem('pickupDateRemoved');

    // Only set default dates if:
    // 1. Dates weren't loaded from localStorage
    // 2. Pickup date wasn't explicitly removed
    // 3. No dates are currently set
    if (!this.datesLoadedFromLocalStorage && pickupDateRemoved !== 'true') {
      if (!this.selectedRequestedPickupDateGt()) {
        this.selectedRequestedPickupDateGt.set(this.getStandardDate(1));
      }

      if (!this.selectedRequestedPickupDateLte()) {
        this.selectedRequestedPickupDateLte.set(this.getStandardDate(1));
      }
    }

    this.tenantKey = this.checkUserRolesService.tenantKey;
    this.isPrintOptionsAllowed.set(this.checkUserRolesService.isPaxOrderPrinter);
    this.isGroupAdmin = this.checkUserRolesService.isGroupAccount;
    this.allowedLocations = this.checkUserRolesService.allowedLocationCodes;

    this.resetOffsetAndOrderList(true);
    this.updateTable();
    this.getCurrentPath();
  }

  /** Add this method to stop event propagation */
  stopClickPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  /** Toggle dropdown menu visibility */
  toggleDropdown(): void {
    this.isDropdownOpen.update(value => !value);
  }

  /** Close dropdown when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdownContainer = target.closest('.dropdown-container');
    if (!dropdownContainer && this.isDropdownOpen()) {
      this.isDropdownOpen.set(false);
    }
  }

  /** Funtion to get the current path */
  getCurrentPath() {
    this.subscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      let navigationEvent = event as NavigationEnd;
      this.currentPath = navigationEvent.url;
    });
  }

  /** Function to set query-params for search fields */
  setQueryParameters() {
    if (this.parcelNumber() !== '') {
      this.queryParameters['parcel-key'] = this.parcelNumber().trim();
    }

    if (this.partnerParcelNumber() !== '') {
      this.queryParameters['partner-parcel-number'] = this.partnerParcelNumber().trim();
    }

    if (this.requestedPickupDateLte() !== '') {
      this.queryParameters['requested-pickup-date-lte'] = this.requestedPickupDateLte().trim();
    }

    if (this.requestedPickupDateGt() !== '') {
      this.queryParameters['requested-pickup-date-gt'] = this.requestedPickupDateGt().trim();
    }

    if (this.requesterName() !== '') {
      this.queryParameters['requester-name'] = this.requesterName().trim();
    }

    if (this.requesterReference() !== '') {
      this.queryParameters['requester-reference'] = this.requesterReference().trim();
    }

    if (this.requesterLocation() !== '') {
      this.queryParameters['requester-location'] = this.requesterLocation().trim();
    }

    if (this.pickupLocation() !== '' && this.isLocationAllowed(this.pickupLocation().trim())) {
        this.queryParameters['pickup-location'] = this.pickupLocation().trim();
    } else if (this.allowedLocations.length > 0) {
      this.queryParameters['pickup-location'] = this.allowedLocations;
    }

    if (this.deliveryLocation() !== '') {
      this.queryParameters['delivery-location'] = this.deliveryLocation().trim();
    }

    if (this.orderStatus() !== '') {
      this.queryParameters['operational-parcel-status'] = this.orderStatus();
    }

    this.queryParameters['limit'] = this.limit;
    this.queryParameters['offset'] = this.offset;
    this.queryParameters['sort'] = this.sortingList();
    if (!this.isGroupAdmin) {
      this.queryParameters['sender-tenant-keys'] = this.tenantKey;
    }

    this.saveFiltersToLocalStorage();
  }

  /** Function to check if search term is in allowed pickup location */
  isLocationAllowed(searchTerm: string): boolean {
    if (this.allowedLocations.length === 0) {
      return true;
    }

    let allowedSearchTerm = false;
    this.allowedLocations.forEach(location => {
      if (location.includes(searchTerm)) {
        allowedSearchTerm = true;
      }
    });
    return allowedSearchTerm;
  }

  /**
   * Helper method to format date for display consistently
   */
  formatDateForDisplay(date: Date | string): string | null {
    if (!date) {
      return null;
    }

    try {
      // If it's already a Date object, format it
      if (date instanceof Date) {
        return this.datePipe.transform(date, 'dd/MM/yyyy');
      }

      // If it's a string, try to parse and format
      const parsedDate = this.parseAndValidateDate(date);
      if (parsedDate) {
        return this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      }

      return null;
    } catch (error) {
      console.error('Error in formatDateForDisplay:', error, 'Input:', date);
      return null;
    }
  }

  removeFilterChip(chip: { key: string, value: string }) {
    this.resetOffsetAndOrderList();
    this.resetSelectionState();
    if (chip.key === 'Remove All Filters') {
      this.removeAllFilters();
      this.updateTable();
      return;
    }

    switch (chip.key) {
      case 'Parcel Number':
        this.parcelNumber.set('');
        break;
      case 'Partner Parcel Number':
        this.partnerParcelNumber.set('');
        break;
      case 'Pick-up Date - Start':
        this.selectedRequestedPickupDateGt.set('');
        localStorage.setItem('pickupDateRemoved', 'true');
        break;
      case 'Pick-up Date - End':
        this.selectedRequestedPickupDateLte.set('');
        localStorage.setItem('pickupDateRemoved', 'true');
        break;
      case 'Pick-up Location':
        this.pickupLocation.set('');
        break;
      case 'Requester Name':
        this.requesterName.set('');
        break;
      case 'Requester Reference':
        this.requesterReference.set('');
        break;
      case 'Requester Location':
        this.requesterLocation.set('');
        break;
      case 'Delivery Location':
        this.deliveryLocation.set('');
        break;
      case 'Order Status':
        this.selectedStatuses.update(statuses => statuses.filter(status => status !== chip.value));
        this.orderStatus.set(this.selectedStatuses().join(','));
        break;
    }

    this.saveFiltersToLocalStorage();
    this.updateTable();
  }

  removeAllFilters() {
    this.resetOffsetAndOrderList(true);
    this.resetSelectionState();
    this.parcelNumber.set('');
    this.partnerParcelNumber.set('');
    this.selectedRequestedPickupDateGt.set('');
    this.selectedRequestedPickupDateLte.set('');
    this.pickupLocation.set('');
    this.requesterName.set('');
    this.requesterReference.set('');
    this.requesterLocation.set('');
    this.deliveryLocation.set('');
    this.selectedStatuses.set([]);
    this.orderStatus.set('');

    localStorage.removeItem('pickupFilters');
    localStorage.setItem('pickupDateRemoved', 'true');

    this.saveFiltersToLocalStorage();
  }

  onRemoveAllFiltersClick() {
    this.removeAllFilters();
    this.updateTable();
  }

  onOrderWithMultipleParcelNumbersClick(index: number) {
    this.expandedRows.update(rows => ({
      ...rows,
      [index]: !rows[index]
    }));
  }

  getParcelStatus(item: any, parcel: string): string {
    if (!item['parcelDetails']) {
      return '';
    }
    const parcelDetail = item['parcelDetails'].find((detail: any) => detail.parcelKey === parcel);
    return parcelDetail ? parcelDetail.operationalParcelStatus : '';
  }

  /** Function to update and refresh the table by any search trigger or scrolling the table and so on... */
  updateTable(): void {
    this.queryParameters = {};
    this.queryParameters['offset'] = this.offset;
    this.queryParameters['limit'] = this.limit;
    this.setQueryParameters();

    const currentRequestId = ++this.lastRequestId;
    this.isTableLoadingMore.set(true);

    this.paxOrderService.getOrders(this.queryParameters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response) => {
        if (currentRequestId !== this.lastRequestId) return; // Skip processing if a newer request has already been initiated

        const newData = response['data'] || [];

        if (newData.length === 0 || newData.length < this.limit) {
          this.allTableDataLoaded.set(true);
        }

        const newDataWithChecked = newData.map((order: any) => {
          let shouldBeChecked = false;

          if (this.offset === 0) {
            shouldBeChecked = this.checkAll();
          } else {
            shouldBeChecked = this.checkAllOriginallySelected;
          }

          return {
            ...order,
            checked: shouldBeChecked
          };
        });

        if (this.offset === 0) {
          this.ordersList.set(newDataWithChecked);
        } else {
          this.ordersList.update(list => list.concat(newDataWithChecked));
        }

        // Expand rows with parcels not in searchedStatuses
        const newExpandedRows: { [key: number]: boolean } = {};
        this.adjustedFilteredOrdersList().forEach((order, i) => {
          if (
            this.selectedStatuses().length > 0 && // Only expand if a status is selected
            order.parcelDetails &&
            order.parcelDetails.some(parcel => !this.selectedStatuses().includes(parcel.operationalParcelStatus))
          ) {
            newExpandedRows[i] = true;
          }
        });
        this.expandedRows.set(newExpandedRows);

        this.limit = response['limit'];
        this.totalFoundItems.set(response['total']);
        this.totalFoundParcels.set(response['totalParcels']);
        this.offset += this.limit;
        setTimeout(() => {
          this.checkScrollAfterDataUpdate();
        }, 100);

        this.isTableLoadingMore.set(false);
        this.dataNotLoaded = false;
      },
      error: (error) => {
        if (currentRequestId !== this.lastRequestId) return; // Ignore outdated response if a newer request was initiated

        this.isTableLoadingMore.set(false);
        this.dataNotLoaded = true;
        this.ordersList.set([]);
        this.totalFoundItems.set(0);
        this.totalFoundParcels.set(0);

        setTimeout(() => {
          if (error.status === 401) {
            this.authorizationFailed.set(true);
          }
        }, 2000);
      }
    });
  }

  onTableScroll(event: any): void {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

    if (atBottom && !this.isTableLoadingMore() && !this.allTableDataLoaded()) {
      this.updateTable();
    }
  }

  checkScrollAfterDataUpdate() {
    const element = document.querySelector('.table-scroll-container') as HTMLElement;
    if (!element) return;

    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    if (atBottom && !this.isTableLoadingMore() && !this.allTableDataLoaded()) {
      this.updateTable();
    }
  }

  resetOffsetAndOrderList(resetCheckAllIntent: boolean = false): void {
    this.offset = 0;
    this.allTableDataLoaded.set(false);
    this.ordersList.set([]);
    // Only reset the original check-all intent when explicitly requested
    if (resetCheckAllIntent) {
      this.checkAllOriginallySelected = false;
    }
  }

  loadCurrentOffsetFromLocalStorage() {
    const savedOffset = localStorage.getItem('offsetForPickups');
    this.offset = savedOffset ? parseInt(savedOffset, 10) : 0;
  }

  saveCurrentOffsetToLocalStorage() {
    localStorage.setItem('offsetForPickups', String(this.offset));
  }

  /** Resizing logic */
  startResize(event: MouseEvent, th: HTMLElement): void {
    this.isResizing = true;
    this.startingX = event.pageX;
    this.startingWidth = th.offsetWidth;

    this.resizingMouseMove = this.performResize.bind(this, th);
    this.resizingMouseUp = this.stopResize.bind(this);

    document.addEventListener('mousemove', this.resizingMouseMove);
    document.addEventListener('mouseup', this.resizingMouseUp);
  }

  performResize(th: HTMLElement, event: MouseEvent): void {
    if (this.isResizing) {
      const movementX = event.pageX - this.startingX;
      th.style.width = `${this.startingWidth + movementX}px`;
    }
  }

  stopResize(): void {
    this.isResizing = false;

    if (this.resizingMouseMove) {
      document.removeEventListener('mousemove', this.resizingMouseMove);
    }
    if (this.resizingMouseUp) {
      document.removeEventListener('mouseup', this.resizingMouseUp);
    }
  }

  /**Function to trigger searching with a specific timeout. */
  onInputChange(event: any) {
    this.resetSelectionState();
    this.resetOffsetAndOrderList(true);
    clearTimeout(this.timerForTyping);
    this.timerForTyping = setTimeout(() => {
      this.updateTable();
    }, 600);
  }

  /**Function to get pickup date input from user*/
  onChangePickUpDate() {
    this.resetSelectionState();
    this.resetOffsetAndOrderList(true);

    // Save to localStorage
    this.saveFiltersToLocalStorage();

    this.timerForTyping = setTimeout(() => {
      this.updateTable();
    }, 600);
  }

  /**
   * Handle manual date input changes (when user types dates directly)
   * Only clear empty inputs immediately, delay validation for partial inputs
   */
  onDateInputChange(event: any, isStartDate: boolean) {
    const inputValue = event.target?.value || event;

    // Only handle empty inputs immediately to clear the date
    if (!inputValue || inputValue.trim() === '') {
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set('');
      } else {
        this.selectedRequestedPickupDateLte.set('');
      }
      this.onChangePickUpDate();
      return;
    }
  }

  /**
   * Handle date blur event - validate and format on focus loss
   */
  onDateBlur(event: any, isStartDate: boolean) {
    // Prevent multiple blur event processing
    if (this.processingDateBlur) {
      return;
    }

    this.processingDateBlur = true;
    const inputValue = event.target?.value;

    if (!inputValue) {
      // Clear the date if input is empty
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set('');
      } else {
        this.selectedRequestedPickupDateLte.set('');
      }
      this.processingDateBlur = false;
      this.onChangePickUpDate();
      return;
    }

    const parsedDate = this.parseAndValidateDate(inputValue);
    if (parsedDate) {
      // Update the model with the parsed date
      if (isStartDate) {
        this.selectedRequestedPickupDateGt.set(parsedDate);
      } else {
        this.selectedRequestedPickupDateLte.set(parsedDate);
      }

      // Use multiple timeouts to forcefully override Angular Material's formatting
      const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
      if (formattedDate && event.target) {
        event.target.value = formattedDate;

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 0);

        setTimeout(() => {
          if (event.target && event.target.value !== formattedDate) {
            event.target.value = formattedDate;
          }
        }, 10);
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);

      this.onChangePickUpDate();
    } else {
      // Invalid date - revert to previous valid value or clear
      console.warn('Invalid date format. Please use DD/MM/YYYY format.');

      const currentDate = isStartDate ? this.selectedRequestedPickupDateGt() : this.selectedRequestedPickupDateLte();

      if (currentDate && currentDate instanceof Date) {
        // Revert to the formatted version of the current valid date
        setTimeout(() => {
          const formattedDate = this.datePipe.transform(currentDate, 'dd/MM/yyyy');
          if (formattedDate && event.target) {
            event.target.value = formattedDate;
          }
        }, 0);
      } else {
        // No valid current date, clear the field
        event.target.value = '';
        if (isStartDate) {
          this.selectedRequestedPickupDateGt.set('');
        } else {
          this.selectedRequestedPickupDateLte.set('');
        }
        this.onChangePickUpDate();
      }

      setTimeout(() => {
        this.processingDateBlur = false;
      }, 50);
    }
  }  /**
   * Handle keydown events for date inputs to provide better UX
   */
  onDateKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (event.key === 'Tab') {
      const target = event.target as HTMLInputElement;
      const isStartDate = target?.id === 'searchRequestedPickupDateGt';

      const currentValue = target?.value;

      event.preventDefault();

      if (currentValue) {
        const parsedDate = this.parseAndValidateDate(currentValue);
        if (parsedDate) {
          const formattedDate = this.datePipe.transform(parsedDate, 'dd/MM/yyyy');
          if (formattedDate && target) {
            target.value = formattedDate;
          }

          if (isStartDate) {
            this.selectedRequestedPickupDateGt.set(parsedDate);
          } else {
            this.selectedRequestedPickupDateLte.set(parsedDate);
          }

          this.onChangePickUpDate();
        }
      }

      setTimeout(() => {
        const focusableElements = Array.from(document.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )) as HTMLElement[];

        const currentIndex = focusableElements.indexOf(target);
        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          focusableElements[currentIndex + 1].focus();
        }
      }, 0);

      return;
    }

    const isNumber = (event.key >= '0' && event.key <= '9');
    const isDot = event.key === '.';
    const isSlash = event.key === '/';

    if (!allowedKeys.includes(event.key) && !isNumber && !isDot && !isSlash) {
      event.preventDefault();
    }
  }

  /**
   * Handle Enter key press on date inputs - validate and apply the date
   */
  onDateEnterPressed(event: any, isStartDate: boolean) {
    // Trigger blur behavior when Enter is pressed to validate the input
    this.onDateBlur(event, isStartDate);
    // Remove focus from the input to hide the datepicker
    event.target.blur();
  }

  resetPickupDatePicker() {
    this.resetSelectionState();
    this.resetOffsetAndOrderList(true);
    this.selectedRequestedPickupDateGt.set('');
    this.selectedRequestedPickupDateLte.set('');
    localStorage.setItem('pickupDateRemoved', 'true');
    this.saveFiltersToLocalStorage();
    this.updateTable();
  }

  /**Pick-up Date functionality :
   without parameter sets standardDate to today,
   with parameter adds the toAddDays to current date.
   The result date will be yyyy-mm-dd */
  getStandardDate(toAddDays: number = 0): string {
    let date = new Date();

    // Set time to noon to avoid timezone issues
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + toAddDays);

    // Adjust for weekends only if the resulting date falls on the weekend
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6) {
      // Saturday, move to Monday
      date.setDate(date.getDate() + 2);
    } else if (dayOfWeek === 0) {
      // Sunday, move to Monday
      date.setDate(date.getDate() + 1);
    }

    // Use DatePipe for consistent formatting
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  /**
   * Function to parse and validate date input handling various formats
   * Supports: Date objects, ISO strings, DD.MM.YYYY, DD/MM/YYYY, YYYY-MM-DD
   * Prevents day/month swapping by enforcing German locale expectations
   */
  parseAndValidateDate(dateInput: Date | string): Date | null {
    if (!dateInput) {
      return null;
    }

    // If already a Date object, validate and return
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // Handle string inputs
    let dateString = dateInput.toString().trim();

    if (!dateString) {
      return null;
    }

    let parsedDate: Date;

    try {
      // Try different date formats with proper validation
      if (dateString.includes('.')) {
        // Handle DD.MM.YYYY format (German format) - PRIORITY FORMAT
        const parts = dateString.split('.');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Validate ranges more strictly
          if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            parsedDate = new Date(year, month, day, 12, 0, 0, 0); // Set to noon to avoid timezone issues

            // Additional validation: ensure the date actually exists (handles Feb 30, etc.)
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD.MM.YYYY format');
        }
      } else if (dateString.includes('/')) {
        // Handle DD/MM/YYYY format - treating as DD/MM/YYYY (day first, European style)
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);

          // Validate ranges
          if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            parsedDate = new Date(year, month, day, 12, 0, 0, 0);

            // Validate the date actually exists
            if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
              return parsedDate;
            } else {
              throw new Error('Invalid date - date does not exist');
            }
          } else {
            throw new Error('Date parts out of range');
          }
        } else {
          throw new Error('Invalid DD/MM/YYYY format');
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Handle YYYY-MM-DD format (ISO format)
        const parts = dateString.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);

        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
          parsedDate = new Date(year, month, day, 12, 0, 0, 0);

          if (parsedDate.getDate() === day && parsedDate.getMonth() === month && parsedDate.getFullYear() === year) {
            return parsedDate;
          } else {
            throw new Error('Invalid date - date does not exist');
          }
        } else {
          throw new Error('Date parts out of range');
        }
      } else {
        // Try standard Date parsing as last resort (handles ISO strings, etc.)
        parsedDate = new Date(dateString);

        // For standard parsing, be extra careful about invalid dates
        if (isNaN(parsedDate.getTime())) {
          throw new Error('Standard parsing failed');
        }

        // Set to noon to avoid timezone issues
        parsedDate.setHours(12, 0, 0, 0);
        return parsedDate;
      }

    } catch (error) {
      console.warn('Date parsing error for input:', dateInput, 'Error:', error.message);
      return null;
    }
  }

  /**Function to return the previous date*/
  getPreviousDate(date: Date | string): string {
    let mainDate: Date;

    // Handle both Date objects and string inputs
    if (date instanceof Date) {
      mainDate = new Date(date);
    } else {
      mainDate = this.parseAndValidateDate(date);
    }

    if (!mainDate || isNaN(mainDate.getTime())) {
      console.error('Invalid date provided to getPreviousDate:', date);
      return '';
    }

    // Create previous day without modifying original date
    let previousDay = new Date(mainDate.getTime());
    previousDay.setDate(previousDay.getDate() - 1);

    // Format as YYYY-MM-DD
    return this.datePipe.transform(previousDay, 'yyyy-MM-dd') || '';
  }

  onCheckAllChange(isCheckAllChecked: boolean) {
    this.checkAll.set(isCheckAllChecked);
    this.checkAllOriginallySelected = isCheckAllChecked; // Track the original intent

    // Clear indeterminate state
    if (this.checkAllRef?.nativeElement) {
      this.checkAllRef.nativeElement.indeterminate = false;
    }

    if (isCheckAllChecked) {
      this.checkedOrdersList = []; // Reset previously checked rows
      this.checkedOrdersListWithDetails = [];
    } else {
      // When unchecking check-all, reset the original intent completely
      this.checkAllOriginallySelected = false;
    }

    // Update checked state in ordersList so computed signal adjustedFilteredOrdersList auto-updates
    this.ordersList.update(list =>
      list.map(item => ({ ...item, checked: isCheckAllChecked }))
    );

    // Update parcel state of parcel checkboxes if they have been open
    setTimeout(() => {
      const elements = document.getElementsByClassName('parcel-checkbox');
      Array.from(elements).forEach(el => {
        (el as HTMLInputElement).checked = isCheckAllChecked;
      });
    });
    // Clear individually checked parcels when check-all is used as either all or none are now selected
    this.checkedParcels = {};
  }

  /** Funtion to uncheck the check-all checkbox */
  uncheckAll(): void {
    this.checkAllRef.nativeElement.checked = false;
  }

  private resetSelectionState(): void {
    this.checkAll.set(false);
    this.checkAllOriginallySelected = false;

    if (this.checkAllRef?.nativeElement) {
      this.checkAllRef.nativeElement.checked = false;
      this.checkAllRef.nativeElement.indeterminate = false;
    }

    this.checkedOrdersList = [];
    this.checkedOrdersListWithDetails = [];
    this.checkedParcels = {};

    this.ordersList.update(list =>
      list.map(item => ({ ...item, checked: false }))
    );
  }

  /** Function to distinguish which checkboxes are checked */
  onCheckboxChange(order: any, checkboxElement: HTMLInputElement): void {
    order.checked = checkboxElement.checked;

    this.ordersList.update(list =>
      list.map(item =>
        item.paxOrderKey === order.paxOrderKey
          ? { ...item, checked: checkboxElement.checked }
          : item
      )
    );

    // Update all parcels belonging to the same order
    Array.from(document.getElementsByClassName(order.paxOrderKey))
      .forEach(el => ((el as HTMLInputElement).checked = order.checked));

    if (order.checked) {
      // If order is checked, remove any individually checked parcels for this order
      delete this.checkedParcels[order.paxOrderKey];
    }

    this.updateCheckedOrdersList();

    // Check the current state of all visible items
    this.syncCheckAllBox();
  }

  isParcelChecked(orderKey: string, parcelKey: string): boolean {
    if (this.checkedOrdersList.some(order => order.paxOrderKey === orderKey)) {
      return true;
    }

    return this.checkedParcels[orderKey]?.has(parcelKey) || false;
  }

  isOrderIndeterminate(order: any): boolean {
    const key = order?.paxOrderKey;
    if (!key || order?.checked) return false;
    return (this.checkedParcels[key]?.size ?? 0) > 0;
  }

  isOrderChecked(order: any): boolean {
    const key = order?.paxOrderKey;
    if (!key) return false;
    return this.checkedOrdersListWithDetails.some((orderItem: any) => orderItem?.paxOrderKey === key);
  }


  syncCheckAllBox() {
    const checkedItems = this.adjustedFilteredOrdersList().filter(item => item.checked);
    const totalItems = this.adjustedFilteredOrdersList().length;

    if (checkedItems.length === 0 && this.getCheckedParcelCount() === 0) {
      // No items checked - uncheck the check-all box and reset intent
      this.uncheckAll();
      this.checkAll.set(false);
      this.checkAllRef.nativeElement.indeterminate = false;
      this.checkAllOriginallySelected = false;
    } else if (this.getCheckedParcelCount() > 0) {
      // Some parcels checked individually - set to indeterminate state
      if (this.checkAllRef?.nativeElement) {
        this.uncheckAll();
        this.checkAll.set(false);
        this.checkAllRef.nativeElement.indeterminate = true;
      }
      // IMPORTANT: Don't change checkAllOriginallySelected here - preserve the original intent
    } else if (checkedItems.length === totalItems) {
      // All visible items are checked
      this.checkAll.set(true);
      // Keep checkAllOriginallySelected as is - don't change it
      this.checkAllRef.nativeElement.indeterminate = false;
      if (this.checkAllRef?.nativeElement) {
        this.checkAllRef.nativeElement.checked = true;
      }
    } else {
      // Some items checked, some not - set to indeterminate state
      if (this.checkAllRef?.nativeElement) {
        this.checkAllRef.nativeElement.indeterminate = true;
        this.checkAllRef.nativeElement.checked = false;
      }
      this.checkAll.set(false);
    }
  }

  onCheckboxChangeSingleParcel(order: any, parcelKey: string, checkboxElement: HTMLInputElement): void {
    const orderKey = order.paxOrderKey;

    if (!this.checkedParcels[orderKey]) {
      this.checkedParcels[orderKey] = new Set<string>();
    }

    if (!order.checked) {
      if (checkboxElement.checked) {
        this.checkedParcels[orderKey].add(parcelKey);
      } else {
        this.checkedParcels[orderKey].delete(parcelKey);
        // If no parcels left checked for this order, remove the key
        if (this.checkedParcels[orderKey].size === 0) {
          delete this.checkedParcels[orderKey];
        }
      }
    } else {
      // User selected order and unchecked a parcel to get all but this one

      order.parcelDetails
        .filter((parcel: any) => parcel.parcelKey !== parcelKey)
        .forEach((parcel: any) => {
          this.checkedParcels[orderKey].add(parcel.parcelKey);
        });
      order.checked = false;
    }

    if (order.parcelDetails.length === this.checkedParcels[orderKey]?.size) {
      order.checked = true;
      delete this.checkedParcels[orderKey];
      Array.from(document.getElementsByClassName('order-' + order.paxOrderKey))
      .forEach(el => ((el as HTMLInputElement).indeterminate = false));
    } else {
      order.checked = false;
      Array.from(document.getElementsByClassName('order-' + order.paxOrderKey))
      .forEach(el => ((el as HTMLInputElement).indeterminate = this.checkedParcels[orderKey]?.size > 0));
    }

    this.updateCheckedOrdersList();
    this.syncCheckAllBox();
  }

  getCheckedParcelCount(): number {
    let count = 0;
    for (const parcels of Object.values(this.checkedParcels)) {
      count += parcels.size;
    }
    return count;
  }

  /** Function to update checked orders list */
  updateCheckedOrdersList(): void {
    this.deactivatePrintButtonInModalBox.set(false);
    this.checkedOrdersList = this.adjustedFilteredOrdersList().filter(order => order.checked);

    const paxOrderKeys = this.checkedOrdersList.map(order => order.paxOrderKey);
    this.checkedOrdersListWithDetails = this.ordersList().filter(order =>
      paxOrderKeys.includes(order.paxOrderKey)
    );
    console.log("checkedOrdersListWithDetails: ", this.checkedOrdersListWithDetails);
  }

  /** Function to triger modal box for "selected" orders*/
  onPrintCheckedOrders(): void {
    if (!this.checkAll() && !this.checkAllOriginallySelected && this.checkedOrdersListWithDetails.length === 0 && Object.keys(this.checkedParcels).length === 0) {
      alert('No orders selected.');
      return;
    }

    if (this.checkAll() || this.checkAllOriginallySelected) {
      if (this.adjustedFilteredOrdersList().length === 0) {
        this.showNoPickupsModal.set(true);
        return;
      }

      const complete = this.adjustedFilteredOrdersList().filter(order =>
        ['COMPLETE'].includes(order.orderStatus)
      );
      const validOrders = this.adjustedFilteredOrdersList().filter(order =>
        !['COMPLETE'].includes(order.orderStatus)
      );

      this.ordersWithCompleteStatus = complete;
      this.ordersProperForPrintList = validOrders;
      const statusFilterIsCompletelyInvalid = this.isOnlyInvalidStatusesSelectedForBulkPrint();
      this.deactivatePrintButtonInModalBox.set(statusFilterIsCompletelyInvalid);
    } else {
      this.getOrdersWithCompleteStatus(this.checkedOrdersListWithDetails);
    }

    this.showModalPrintChecked.set(true);
  }

  /** Function to filter faulty orders */
  async printOnlyValidOrders() {
    this.closeModal();
    this.showSpinner.set(true);
    this.messageVisible.set(false);

    if (this.checkAll() || this.checkAllOriginallySelected) {
      const bulkContext = this.buildBulkPrintContext();
      const uniqueFaultyOrderKeys = Array.from(new Set(this.faultyOrder));
      const excludedKeys = Array.from(new Set([
        ...bulkContext.excludedKeys,
        ...uniqueFaultyOrderKeys
      ]));

      if (excludedKeys.length > 0) {
        bulkContext.bulkPostData.excludedPaxOrderKeys = excludedKeys;
      }

      this.paxOrderService.bulkPrint(bulkContext.bulkPostData, this.selectedFormat)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
        next: (response: Blob) => {
          const blob = new Blob([response], { type: this.selectedFormat === 'zpl' ? 'text/plain' : 'application/pdf' });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = 'label.' + this.selectedFormat;
          link.click();

          if (bulkContext.partialParcelOrderKeys.length > 0) {
            this.printOrder(bulkContext.partialParcelOrderKeys, bulkContext.partialParcelKeys, this.selectedFormat);
          }

          this.messageText.set("Bulk print is successfully done!");
          this.printEventText.set("Print event sent to UniQue may take time to reflect.");
          this.printSucessfull.set(true);

          this.resetSelectionState();
          this.resetOffsetAndOrderList(true);
          this.updateTable();

          this.showSpinner.set(false);
          this.messageVisible.set(true);
        },
        error: async (error) => {
          this.printSucessfull.set(false);
          if (error.error instanceof Blob) {
            try {
              const text = await error.error.text();
              const errorBody = JSON.parse(text);
              this.messageText.set(errorBody.message || 'Bulk printing failed due to faulty pickups.');
            } catch (e) {
              this.messageText.set('Error parsing error response.');
            }
          } else {
            this.messageText.set('Error: ' + error.message);
          }

          this.showSpinner.set(false);
          this.messageVisible.set(true);
        }
      });
      return;
    }

    const paxOrderKeysToPrint = this.checkedOrdersList
      .map(order => order.paxOrderKey)
      .filter(paxOrderKey =>
        !this.faultyOrder.some(fault => fault.includes(paxOrderKey))
      );

    this.paxOrderService.printOrders({ paxOrderKeys: paxOrderKeysToPrint }, this.selectedFormat)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: this.selectedFormat === 'zpl' ? 'text/plain' : 'application/pdf' });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = 'label.' + this.selectedFormat;
        link.click();

        this.messageText.set("Print is successfully done!");
        this.printEventText.set("Print event sent to UniQue may take time to reflect.");
        this.printSucessfull.set(true);

        this.resetSelectionState();
        this.resetOffsetAndOrderList(true);
        this.updateTable();

        this.showSpinner.set(false);
        this.messageVisible.set(true);
      },
      error: async (error) => {
        this.printSucessfull.set(false);
        if (error.error instanceof Blob) {
          try {
            const text = await error.error.text();
            const errorBody = JSON.parse(text);
            this.messageText.set(errorBody.message || 'Printing failed due to faulty pickups.');
          } catch (e) {
            this.messageText.set('Error parsing error response.');
          }
        } else {
          this.messageText.set('Error: ' + error.message);
        }

        this.showSpinner.set(false);
        this.messageVisible.set(true);
      }
    });
  }

  isOnlyInvalidStatusesSelectedForBulkPrint(): boolean {
    if (!this.checkAll()) return false;

    const selectedStatuses = (this.queryParameters['operational-parcel-status'] || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const allowedStatuses = ['NEW', 'IN_PROGRESS', 'PICKED_UP', 'CANCELLED'];
    return selectedStatuses.length > 0 && selectedStatuses.every(s => !allowedStatuses.includes(s));
  }

  /** Function to print the order label */
  printOrder(paxOrderKeys, parcelKeys, labelFormat: string): void {
    const postData: any = {
      'paxOrderKeys': paxOrderKeys
    }

    if (parcelKeys && parcelKeys.length > 0) {
      postData['parcelKeys'] = parcelKeys;
    }

    this.paxOrderService.printOrders(postData, labelFormat)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: labelFormat === 'zpl' ? 'text/plain' : 'application/pdf' });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = 'label.' + labelFormat;
        link.click();

        // Explicitly complete the observable
        this.messageText.set("Print is successfully done!");
        this.printEventText.set("Please note that the print event sent to UniQue will take time to process and won't be immediately available.");
        this.printSucessfull.set(true);

        this.resetSelectionState();
        // Refresh table
        this.resetOffsetAndOrderList(true);
        this.updateTable();
      },
      error: async (error) => {
        // Check for 503 Service Unavailable error first
        if (error?.status === 503) {
          this.messageText.set('Service is currently not available! Please try later again.');
          this.printSucessfull.set(false);
          this.showSpinner.set(false);
          this.messageVisible.set(true);
          return;
        }

        this.faultyOrder = [];
        if (error.error instanceof Blob) {
          try {
            const text = await error.error.text();
            const errorBody = JSON.parse(text);
            this.faultyOrder = errorBody.errors.map((err: any) => err.fieldValue) || [];
          } catch (e) {
            // If parsing fails, check status again or show generic error
            if (error?.status === 503) {
              this.messageText.set('Service is currently not available! Please try later again.');
            } else {
              this.messageText.set('Printing failed. Please try again later.');
            }
            this.printSucessfull.set(false);
            this.showSpinner.set(false);
            this.messageVisible.set(true);
            return;
          }
        } else {
          // For non-Blob errors, check if it's a 503 or show the error message
          if (error?.status === 503) {
            this.messageText.set('Service is currently not available! Please try later again.');
          } else {
            this.messageText.set('Printing failed. Please try again. Error: ' + (error?.message || 'Unknown error'));
          }
          this.printSucessfull.set(false);
          this.showSpinner.set(false);
          this.messageVisible.set(true);
          return;
        }

        this.messageText.set('Print was not successful due to the following faulty orders: ');
        this.printSucessfull.set(false);
        this.showSpinner.set(false);
        this.messageVisible.set(true);
      },
      complete: () => {
        // This will be called if the observable completes naturally
        this.messageText.set("Print is successfully done!");
        this.printEventText.set("Please note that the print event sent to UniQue will take time to process and won't be immediately available.");
        this.printSucessfull.set(true);
      }
    });
  }

  private buildBulkPrintContext(): {
    bulkPostData: any;
    excludedKeys: string[];
    partialParcelOrderKeys: string[];
    partialParcelKeys: string[];
    estimatedSelectedCount: number;
  } {
    // grab the current list of active sort keys
    const sort = [...this.sortingList()];

    // Extract clean and relevant fields from queryParameters
    const {
      'parcel-key': parcelKey,
      'partner-parcel-number': partnerParcelNumber,
      'requested-pickup-date-gt': requestedPickupDateGt,
      'requested-pickup-date-lte': requestedPickupDateLte,
      'requester-name': requesterName,
      'requester-reference': requesterReference,
      'requester-location': requesterLocation,
      'pickup-location': pickupLocation,
      'delivery-location': deliveryLocation,
      'operational-parcel-status': operationalOrderStatus,
      'sender-tenant-keys': senderTenantKeyRaw
    } = this.queryParameters;

    const ALLOWED_STATUSES = ['NEW', 'IN_PROGRESS', 'PICKED_UP', 'CANCELLED'];
    const rawStatuses = (operationalOrderStatus ?? '')
      .split(',')
      .filter(Boolean);
    let statusList = rawStatuses.filter(s =>
      ALLOWED_STATUSES.includes(s)
    );
    if (statusList.length === 0) {
      statusList = [...ALLOWED_STATUSES];
    }

    const excludedKeys: string[] = [];

    // Track pickups with partial parcel selections for a single combined print call
    const partialParcelOrderKeys: string[] = [];
    const partialParcelKeys: string[] = [];

    // Collect all unchecked items from the loaded list as exclusions
    this.adjustedFilteredOrdersList()
      .filter(order => !order.checked)
      .forEach(order => excludedKeys.push(order.paxOrderKey));

    // Handle multi-parcel pickups with partial selections
    for (const orderKey in this.checkedParcels) {
      const parcelKeys = Array.from(this.checkedParcels[orderKey]);
      if (parcelKeys.length > 0) {
        excludedKeys.push(orderKey);
        partialParcelOrderKeys.push(orderKey);
        partialParcelKeys.push(...parcelKeys);
      }
    }

    //Pickup location is a special case as API expects an array but UI provides a single value, so we need to handle it separately
    const pickupLocationValue = this.pickupLocation()?.trim();

    const bulkPostData: any = {
      parcelKey,
      partnerParcelNumber,
      requestedPickUpDateGt: requestedPickupDateGt,
      requestedPickUpDateLte: requestedPickupDateLte,
      requesterName,
      requesterReference,
      requesterLocation,
      ...(pickupLocationValue ? { pickupLocation: [pickupLocationValue] } : {}),
      deliveryLocation,
      operationalOrderStatus: statusList,
      senderTenantKeys: this.isGroupAdmin ? GROUP_ACCOUNT_SENDER_TENANT_KEYS : [senderTenantKeyRaw],
      limit: 1000,
      offset: 0,
      sort
    };

    const uniqueExcludedKeys = Array.from(new Set(excludedKeys));
    const estimatedSelectedCount = Math.max(this.totalFoundItems() - uniqueExcludedKeys.length, 0);

    return {
      bulkPostData,
      excludedKeys: uniqueExcludedKeys,
      partialParcelOrderKeys,
      partialParcelKeys,
      estimatedSelectedCount
    };
  }

  private setBulkPrintCounts(estimatedSelectedCount: number, faultyOrderKeys: string[]) {
    const uniqueFaulty = Array.from(new Set(faultyOrderKeys));
    const validCount = Math.max(estimatedSelectedCount - uniqueFaulty.length, 0);
    this.ordersProperForPrintList = Array.from({ length: validCount });
  }

  /**Function to print */
  showSpinner = signal<boolean>(false);
  printSucessfull = signal<boolean>(false);
  messageVisible = signal<boolean>(false);
  fadeOut = signal<boolean>(false);
  messageText = signal<string>('');
  printEventText = signal<string>('');
  spinnerDuration: number = 3000;
  showNoPickupsModal = signal<boolean>(false);

  onPrintCheckedOrdersInModalBox() {
    this.closeModal();
    this.showSpinner.set(true);
    this.messageVisible.set(false);

    // Use checkAllOriginallySelected to determine if bulk print should be used
    // This handles the case where user clicked check-all, then unchecked some items
    // In that case, checkAll() is false, but we still need to use bulk print with exclusions
    if (this.checkAll() || this.checkAllOriginallySelected) {
      const bulkContext = this.buildBulkPrintContext();
      this.lastBulkPrintContext = bulkContext;

      // Add excludedPaxOrderKeys if we have items to exclude
      if (bulkContext.excludedKeys.length > 0) {
        bulkContext.bulkPostData.excludedPaxOrderKeys = bulkContext.excludedKeys;
      }

      this.paxOrderService.bulkPrint(bulkContext.bulkPostData, this.selectedFormat)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
        next: (response: Blob) => {
          const blob = new Blob([response], { type: this.selectedFormat === 'zpl' ? 'text/plain' : 'application/pdf' });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = 'label.' + this.selectedFormat;
          link.click();

          //  After bulk print completes, print pickups with partial parcel selections
          // These were excluded from bulk print and need to be printed in a single call with specific parcel keys
          if (bulkContext.partialParcelOrderKeys.length > 0) {
            // Make a single print call with all order keys and all their specific parcel keys
            this.printOrder(bulkContext.partialParcelOrderKeys, bulkContext.partialParcelKeys, this.selectedFormat);
          }

          this.messageText.set("Bulk print is successfully done!");
          this.printEventText.set("Print event sent to UniQue may take time to reflect.");
          this.printSucessfull.set(true);

          this.resetSelectionState();
          this.resetOffsetAndOrderList(true);

          this.updateTable();

          this.lastBulkPrintContext = null;

          this.showSpinner.set(false);
          this.messageVisible.set(true);
        },
        error: async (error) => {
          // Check for 503 Service Unavailable error first
          if (error?.status === 503) {
            this.messageText.set('Service is currently not available! Please try later again.');
            this.printSucessfull.set(false);
            this.showSpinner.set(false);
            this.messageVisible.set(true);
            return;
          }
          if (error?.status === 404) {
            this.messageText.set('Requested data not found. Please refresh the page and try again.');
            this.printSucessfull.set(false);
            this.showSpinner.set(false);
            this.messageVisible.set(true);
            return;
          }

          this.faultyOrder = [];
          if (error.error instanceof Blob) {
            let rawErrorText = '';
            try {
              rawErrorText = await error.error.text();
              const errorBody = JSON.parse(rawErrorText);
              this.faultyOrder = errorBody.errors.map((err: any) => err.fieldValue) || [];
              this.setBulkPrintCounts(bulkContext.estimatedSelectedCount, this.faultyOrder);
            } catch (e) {
              // If parsing fails, check status again or show generic error
              if (error?.status === 503) {
                this.messageText.set('Service is currently not available! Please try later again.');
              } else {
                const originalError = (rawErrorText || error?.message || 'Unknown error').toString().slice(0, 300);
                this.messageText.set(`Bulk printing failed. Please try again later. Original error: ${originalError}`);
              }
              this.printSucessfull.set(false);
              this.showSpinner.set(false);
              this.messageVisible.set(true);
              return;
            }
          } else {
            // For non-Blob errors, check if it's a 503 or show the error message
            if (error?.status === 503) {
              this.messageText.set('Service is currently not available! Please try later again.');
            } else {
              this.messageText.set('Bulk printing failed. Please try again. Error: ' + (error?.message || 'Unknown error'));
            }
            this.printSucessfull.set(false);
            this.showSpinner.set(false);
            this.messageVisible.set(true);
            return;
          }

          this.messageText.set('Bulk print failed due to the following faulty pickups.');
          this.printSucessfull.set(false);
          this.showSpinner.set(false);
          this.messageVisible.set(true);
        }
      });

    }
    else if (Object.keys(this.checkedParcels).length > 0) {
      this.spinnerWithMessage();

      if (this.checkedOrdersList.length > 0) {
        this.spinnerWithMessage();
        const uniquePaxOrderKeys = [...new Set(this.checkedOrdersList.map(order => order.paxOrderKey))];
        this.printOrder(uniquePaxOrderKeys, null, this.selectedFormat);
      }

      const uniquePaxOrderKeysFromParcels = Object.keys(this.checkedParcels);
      const parcelKeys = Object.values(this.checkedParcels).map(set => Array.from(set)).flat();
      this.printOrder(uniquePaxOrderKeysFromParcels, parcelKeys, this.selectedFormat);
    } else {
      this.spinnerWithMessage();
      const uniquePaxOrderKeys = [...new Set(this.checkedOrdersList.map(order => order.paxOrderKey))];
      this.printOrder(uniquePaxOrderKeys, null, this.selectedFormat);
    }
  }

  spinnerWithMessage() {
    this.closeModal();
    this.showSpinner.set(true);
    setTimeout(() => {
      this.showSpinner.set(false);
      this.showMessage();
    }, this.spinnerDuration);
  }

  private showMessage() {
    this.messageVisible.set(true);
  }

  /** Function to clode modal after by clicking out of the modal */
  onBackdropClick(event: MouseEvent): void {
    this.closeModal();
  }

  /** Function to stop propagation on modal */
  onModalContentClick(event: MouseEvent): void {
    event.stopPropagation();
  }
  /** Function to close modal */
  closeModal(): void {
    this.showModalPrintChecked.set(false);
    this.messageVisible.set(false);
    this.showNoPickupsModal.set(false);
  }

  getOrdersWithCompleteStatus(orders): { ordersWithCompleteStatus, ordersProperForPrintList } {
    this.ordersWithCompleteStatus = [];
    this.ordersProperForPrintList = [];
    for (let order of orders) {
      if (order['operationalOrderStatus'] === 'COMPLETE') {
        this.ordersWithCompleteStatus.push(order);
      }
      else {
        this.ordersProperForPrintList.push(order);
      }
    }

    if (this.ordersProperForPrintList.length === 0 && this.getCheckedParcelCount() === 0) {
      this.deactivatePrintButtonInModalBox.set(true);
    }
    return { ordersWithCompleteStatus: this.ordersWithCompleteStatus, ordersProperForPrintList: this.ordersProperForPrintList }
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Texted copied to clipboard!');
    }).catch(err => {
      console.error('Error while copying text to clipboard!: ', err);
    });
  }

  onCopyTableToClipboard() {
    // Close dropdown
    this.isDropdownOpen.set(false);

    // Check if any individual rows are selected OR if there are individually selected parcels
    const checkedItems = this.adjustedFilteredOrdersList().filter(item =>
      item.checked || this.checkedParcels[item.paxOrderKey]
    );

    // Scenario 1: Nothing is checked
    if (checkedItems.length === 0) {
      this.showCopyFadingMessage('No Pickup is selected to copy to clipboard', 'error');
      return;
    }

    // Calculate the estimated number of parcels to be copied
    let estimatedParcelCount: number;
    if (this.checkAllOriginallySelected) {
      // For check-all scenario: total parcels minus parcels from unchecked items
      const uncheckedItems = this.adjustedFilteredOrdersList().filter(item => !item.checked);
      const uncheckedParcelCount = this.countParcelsInItems(uncheckedItems);
      estimatedParcelCount = this.totalFoundParcels() - uncheckedParcelCount;
    } else {
      // For individual selection: count parcels from checked items
      estimatedParcelCount = this.countParcelsInItems(checkedItems);
    }

    // Show warning if check-all and exceeds limit
    if (this.checkAllOriginallySelected && estimatedParcelCount > MAX_CLIPBOARD_RECORDS) {
      this.showCopyFadingMessage(
        `Note: Copy to clipboard is limited to ${MAX_CLIPBOARD_RECORDS} parcels`,
        'success'
      );
    }

    this.performCopyOperation(checkedItems, estimatedParcelCount);
  }

  private performCopyOperation(checkedItems: any[], estimatedParcelCount: number) {
    // Scenario 2: Check-all was originally selected (regardless of current state) - copy all items excluding unchecked ones
    if (this.checkAllOriginallySelected) {
      if (estimatedParcelCount > MAX_CLIPBOARD_RECORDS) {
        this.shouldLimitClipboardCopy.set(true);
      }
      this.copyAllItemsToClipboard();
      return;
    }

    // Scenario 3: Only individual items were selected (no check-all intent)
    this.copySelectedItemsToClipboard(checkedItems);
  }

  private async copySelectedItemsToClipboard(checkedItems: any[]) {
    this.showCopySpinner.set(true);
    const csvContent = this.generateCsvContent(checkedItems);

    try {
      await this.safeWriteToClipboard(csvContent);
      console.log('Selected items copied to clipboard!');
      this.showCopySpinner.set(false);
      const parcelCount = this.countParcelsInItems(checkedItems);
      this.showCopySuccessMessage(parcelCount);
      this.resetSelectionState();
    } catch (err) {
      console.error('Error while copying selected items to clipboard: ', err);
      this.showCopySpinner.set(false);
      this.showCopyErrorMessage();
    }
  }

  private async safeWriteToClipboard(text: string): Promise<void> {
    const maxRetries = 3;
    const retryDelay = 300;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // First ensure document has focus
        if (!document.hasFocus()) {
          window.focus();
          // Give more time for focus to be established
          await new Promise(resolve => setTimeout(resolve, 150));
        }

        // Try the modern clipboard API first
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
          return; // Success!
        }

        // If clipboard API is not available, fall back to execCommand
        throw new Error('Modern clipboard API not available, trying fallback');

      } catch (error) {
        console.warn(`Clipboard attempt ${attempt} failed:`, error);

        // On final attempt, try the legacy fallback method
        if (attempt === maxRetries) {
          try {
            await this.fallbackCopyToClipboard(text);
            return; // Success with fallback!
          } catch (fallbackError) {
            console.error('All clipboard methods failed:', fallbackError);
            // Don't throw - just show error message and continue
            return;
          }
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }

  private async fallbackCopyToClipboard(text: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Create a textarea that can receive focus and selection
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // Position it off-screen but make it focusable
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      textArea.style.opacity = '0';
      textArea.setAttribute('readonly', '');
      textArea.setAttribute('aria-hidden', 'true');

      document.body.appendChild(textArea);

      try {
        // Ensure the textarea is focused and selected
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);

        // Try to copy using execCommand
        const successful = document.execCommand('copy');
        if (successful) {
          resolve();
        } else {
          reject(new Error('execCommand copy returned false'));
        }
      } catch (err) {
        reject(err);
      } finally {
        // Clean up
        document.body.removeChild(textArea);
      }
    });
  }

  private async copyAllItemsToClipboard() {
    this.showCopySpinner.set(true);
    try {
      const allItems = await this.fetchAllItemsForCopyToClipboard();

      // Get the paxOrderKeys of unchecked items from the currently loaded list
      // Also exclude items that only have individual parcel selections
      const uncheckedOrderKeys = this.adjustedFilteredOrdersList()
        .filter(item => !item.checked && !this.checkedParcels[item.paxOrderKey])
        .map(item => item.paxOrderKey);

      // Filter out the unchecked items from all items
      const filteredItems = allItems.filter(item => !uncheckedOrderKeys.includes(item.paxOrderKey));

      // Mark items with individual parcel selections
      filteredItems.forEach(item => {
        if (this.checkedParcels[item.paxOrderKey]) {
          // Mark as not fully checked so generateCsvContent knows to filter parcels
          item.checked = false;
        }
      });

      let itemsToCopy = filteredItems;
      if (this.shouldLimitClipboardCopy()) {
        itemsToCopy = this.limitItemsToMaxParcels(filteredItems, MAX_CLIPBOARD_RECORDS);
      }

      const csvContent = this.generateCsvContent(itemsToCopy);

      await this.safeWriteToClipboard(csvContent);
      console.log('All items copied to clipboard!');

      const parcelCount = this.countParcelsInItems(itemsToCopy);

      this.showCopySpinner.set(false);

      let successMessage = `All ${parcelCount} parcel${parcelCount === 1 ? '' : 's'} copied to clipboard successfully!`;
      if (this.shouldLimitClipboardCopy()) {
        successMessage = `The first ${parcelCount} parcel${parcelCount === 1 ? '' : 's'} copied to clipboard successfully!`;
      }

      this.showCopyFadingMessage(successMessage, 'success');
      this.resetSelectionState();
      this.shouldLimitClipboardCopy.set(false);
    } catch (error) {
      console.error('Error while copying all items to clipboard: ', error);
      this.showCopySpinner.set(false);
      this.showCopyErrorMessage();
      this.shouldLimitClipboardCopy.set(false);
    }
  }

  private async fetchAllItemsForCopyToClipboard(): Promise<any[]> {
    const allItems: any[] = [];
    const totalItems = this.totalFoundItems();
    const limit = this.limit;
    let currentOffset = 0;

    while (currentOffset < totalItems) {
      try {
        const queryParams = { ...this.queryParameters };
        queryParams['offset'] = currentOffset;
        queryParams['limit'] = limit;

        const response = await firstValueFrom(this.paxOrderService.getOrders(queryParams));
        const newData = response['data'] || [];

        // Convert to optimized format - only fields needed for CSV generation
        const adjustedItems = newData.map((order: any) => {
          return {
            paxOrderKey: order.paxOrderKey,
            parcelNumber: order.parcelDetails?.[0]?.parcelKey || '',
            pickUpDate: order.requestedPickupDate,
            requesterName: order.requester['name'],
            requesterLocation: order['requesterLocation'],
            pickupLocation: order['pickupLocation'],
            deliveryLocation: order['deliveryLocation'],
            orderStatus: order['operationalOrderStatus'],
            parcelDetails: order.parcelDetails || []
          };
        });

        allItems.push(...adjustedItems);
        currentOffset += limit;

        // Break if we got less data than expected (end of results)
        if (newData.length < limit) {
          break;
        }
      } catch (error) {
        console.error('Error fetching data at offset', currentOffset, ':', error);
        throw error;
      }
    }

    return allItems;
  }

  /**
   * Helper method to count total parcels from a list of orders
   * Takes into account individually selected parcels from multi-parcel orders
   */
  private countParcelsInItems(items: any[]): number {
    return items.reduce((total, item) => {
      const orderKey = item.paxOrderKey;

      // If this order has individually selected parcels (and not fully checked)
      if (!item.checked && this.checkedParcels[orderKey]) {
        return total + this.checkedParcels[orderKey].size;
      }

      // Otherwise count all parcels in the order
      const parcelCount = item['parcelDetails'] && item['parcelDetails'].length > 0
        ? item['parcelDetails'].length
        : 0;
      return total + parcelCount;
    }, 0);
  }

  private limitItemsToMaxParcels(items: any[], maxParcels: number): any[] {
    const result: any[] = [];
    let parcelCount = 0;

    for (const item of items) {
      const orderKey = item.paxOrderKey;
      let itemParcelCount = 0;

      if (!item.checked && this.checkedParcels[orderKey]) {
        itemParcelCount = this.checkedParcels[orderKey].size;
      } else {
        itemParcelCount = item['parcelDetails'] && item['parcelDetails'].length > 0
          ? item['parcelDetails'].length
          : 0;
      }

      if (parcelCount + itemParcelCount <= maxParcels) {
        result.push(item);
        parcelCount += itemParcelCount;
      } else if (parcelCount < maxParcels) {
        const remainingSlots = maxParcels - parcelCount;
        const itemCopy = { ...item };

        if (!itemCopy.checked && this.checkedParcels[orderKey]) {
          const selectedParcels = Array.from(this.checkedParcels[orderKey]);
          this.checkedParcels[orderKey] = new Set(selectedParcels.slice(0, remainingSlots));
        } else if (itemCopy['parcelDetails']) {
          itemCopy['parcelDetails'] = itemCopy['parcelDetails'].slice(0, remainingSlots);
        }

        result.push(itemCopy);
        parcelCount = maxParcels;
      }

      if (parcelCount >= maxParcels) {
        break;
      }
    }

    return result;
  }

  private generateCsvContent(items: any[]): string {
    let csvContent = '';

    items.forEach((item: any) => {
      const orderKey = item.paxOrderKey;
      const hasIndividuallySelectedParcels = !item.checked && this.checkedParcels[orderKey];

      // For orders with multiple parcels
      if (item['parcelDetails'] && item['parcelDetails'].length > 1) {
        let parcelsToInclude: any[];

        if (hasIndividuallySelectedParcels) {
          // Only include individually selected parcels
          parcelsToInclude = item['parcelDetails'].filter((parcel: any) =>
            this.checkedParcels[orderKey].has(parcel.parcelKey)
          );
        } else {
          // Include all parcels (order is fully checked)
          parcelsToInclude = item['parcelDetails'];
        }

        this.sortParcelDetailsBySelectedStatus(parcelsToInclude).forEach((parcel: any) => {
          const row = [
            parcel.parcelKey || '',
            item['pickUpDate'] ? this.datePipe.transform(item['pickUpDate'], 'dd.MM.yyyy') : '',
            item['requesterName'] || '',
            item['requesterLocation'] || '',
            item['pickupLocation'] || '',
            item['deliveryLocation'] || '',
            this.getParcelStatus(item, parcel.parcelKey) || ''
          ];
          csvContent += row.join('\t') + '\n';
        });
      } else {
        // Single parcel order
        const parcelKey = item['parcelDetails'] && item['parcelDetails'].length > 0
          ? item['parcelDetails'][0]['parcelKey']
          : '';
        const row = [
          parcelKey,
          item['pickUpDate'] ? this.datePipe.transform(item['pickUpDate'], 'dd.MM.yyyy') : '',
          item['requesterName'] || '',
          item['requesterLocation'] || '',
          item['pickupLocation'] || '',
          item['deliveryLocation'] || '',
          item['orderStatus'] || ''
        ];
        csvContent += row.join('\t') + '\n';
      }
    });

    return csvContent;
  }

  showCopySuccessMessage(count?: number) {
    const message = count ?
      `${count} selected parcel${count === 1 ? '' : 's'} copied to clipboard successfully!` :
      'Current loaded table data copied to clipboard successfully!';
    this.showCopyFadingMessage(message, 'success');
  }

  showCopyErrorMessage() {
    this.showCopyFadingMessage('Failed to copy table data to clipboard!', 'error');
  }

  showCopyFadingMessage(text: string, type: 'success' | 'error') {
    this.copyMessage.set(text);
    this.copyMessageType.set(type);
    this.copyDisplayDuration = 3000;
    this.copyFadeDuration = 1000;
    // Increment trigger to force popup to appear even with same message
    this.copyMessageTrigger.set(Date.now());
  }

  onSeeOrderDetailsClick(item: any) {
    let selectedOrder = this.ordersList().filter((order: any) => order['paxOrderKey'] === item.paxOrderKey);
    localStorage.setItem("seePaxOrderDetails", JSON.stringify(selectedOrder[0]));
    this.saveCurrentOffsetToLocalStorage();
    this.router.navigate(['/order-details'], { state: { fromMyPickups: true } });
  }

  onOrderStatusSelect(selectedStatuses: string[]) {
    this.resetSelectionState();
    this.resetOffsetAndOrderList(true);
    this.selectedStatuses.set(selectedStatuses);
    this.orderStatus.set(this.selectedStatuses().join(','));
    this.updateTable();
  }

  stopEvent(event: Event) {
    event.stopPropagation();
  }

  saveFiltersToLocalStorage() {
    const filters = {
      parcelNumber: this.parcelNumber(),
      partnerParcelNumber: this.partnerParcelNumber(),
      // Convert Date objects to YYYY-MM-DD format for reliable storage/retrieval
      selectedRequestedPickupDateGt: this.selectedRequestedPickupDateGt() instanceof Date
        ? this.datePipe.transform(this.selectedRequestedPickupDateGt(), 'yyyy-MM-dd')
        : this.selectedRequestedPickupDateGt(),
      selectedRequestedPickupDateLte: this.selectedRequestedPickupDateLte() instanceof Date
        ? this.datePipe.transform(this.selectedRequestedPickupDateLte(), 'yyyy-MM-dd')
        : this.selectedRequestedPickupDateLte(),
      requesterName: this.requesterName(),
      requesterLocation: this.requesterLocation(),
      pickupLocation: this.pickupLocation(),
      deliveryLocation: this.deliveryLocation(),
      selectedStatuses: this.selectedStatuses(),
      orderStatus: this.orderStatus(),
      requesterReference: this.requesterReference(),
    };
    localStorage.setItem('pickupFilters', JSON.stringify(filters));
  }

  loadFiltersFromLocalStorage() {
    const storedFilters = localStorage.getItem('pickupFilters');
    this.datesLoadedFromLocalStorage = false;

    if (storedFilters) {
      try {
        const filters = JSON.parse(storedFilters);
        this.parcelNumber.set(filters.parcelNumber || '');
        this.partnerParcelNumber.set(filters.partnerParcelNumber || '');

        // Parse stored date strings back to Date objects
        if (filters.selectedRequestedPickupDateGt) {
          this.datesLoadedFromLocalStorage = true;
          const parsedStartDate = this.parseAndValidateDate(filters.selectedRequestedPickupDateGt);
          if (parsedStartDate) {
            this.selectedRequestedPickupDateGt.set(parsedStartDate);
          } else {
            this.selectedRequestedPickupDateGt.set('');
          }
        } else {
          this.selectedRequestedPickupDateGt.set('');
        }

        if (filters.selectedRequestedPickupDateLte) {
          this.datesLoadedFromLocalStorage = true;
          const parsedEndDate = this.parseAndValidateDate(filters.selectedRequestedPickupDateLte);
          if (parsedEndDate) {
            this.selectedRequestedPickupDateLte.set(parsedEndDate);
          } else {
            this.selectedRequestedPickupDateLte.set('');
          }
        } else {
          this.selectedRequestedPickupDateLte.set('');
        }

        this.pickupLocation.set(filters.pickupLocation || '');
        this.requesterName.set(filters.requesterName || '');
        this.requesterReference.set(filters.requesterReference || '');
        this.requesterLocation.set(filters.requesterLocation || '');
        this.deliveryLocation.set(filters.deliveryLocation || '');
        this.selectedStatuses.set(filters.selectedStatuses || []);
        this.orderStatus.set(filters.orderStatus || '');
      } catch (error) {
        console.error('Error parsing stored filters:', error);
        // Reset to defaults if parsing fails
        localStorage.removeItem('pickupFilters');
      }
    }
  }

  onParcelNumberSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "parcelnumber";
    const descendingKey = "-parcelnumber";
    const currentList = this.sortingList();
    const existingIndex = currentList.findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isParcelNumberAscendingSortingActive()) {
        this.sortingList.update(list => list.filter((_, idx) => idx !== existingIndex));
    } else if (this.isParcelNumberDescendingSortingActive()) {
        this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? ascendingKey : item));
    } else {
        if (existingIndex > -1) {
            this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? descendingKey : item));
        } else {
            this.sortingList.update(list => [...list, descendingKey]);
        }
    }

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }

  onPickupDateSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "pickupdate";
    const descendingKey = "-pickupdate";
    const currentList = this.sortingList();
    const existingIndex = currentList.findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isPickupDateAscendingSortingActive()) {
        this.sortingList.update(list => list.filter((_, idx) => idx !== existingIndex));
    } else if (this.isPickupDateDescendingSortingActive()) {
        this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? ascendingKey : item));
    } else {
        if (existingIndex > -1) {
            this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? descendingKey : item));
        } else {
            this.sortingList.update(list => [...list, descendingKey]);
        }
    }

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }

  onRequesterLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "requesterlocation";
    const descendingKey = "-requesterlocation";
    const currentList = this.sortingList();
    const existingIndex = currentList.findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isRequesterLocationAscendingSortingActive()) {
        this.sortingList.update(list => list.filter((_, idx) => idx !== existingIndex));
    } else if (this.isRequesterLocationDescendingSortingActive()) {
        this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? ascendingKey : item));
    } else {
        if (existingIndex > -1) {
            this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? descendingKey : item));
        } else {
            this.sortingList.update(list => [...list, descendingKey]);
        }
    }

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }

  onPickupLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "pickuplocation";
    const descendingKey = "-pickuplocation";
    const currentList = this.sortingList();
    const existingIndex = currentList.findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isPickupLocationAscendingSortingActive()) {
        this.sortingList.update(list => list.filter((_, idx) => idx !== existingIndex));
    } else if (this.isPickupLocationDescendingSortingActive()) {
        this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? ascendingKey : item));
    } else {
        if (existingIndex > -1) {
            this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? descendingKey : item));
        } else {
            this.sortingList.update(list => [...list, descendingKey]);
        }
    }

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }


  onDeliveryLocationSortingClick() {
    this.resetOffsetAndOrderList();
    const ascendingKey = "deliverylocation";
    const descendingKey = "-deliverylocation";
    const currentList = this.sortingList();
    const existingIndex = currentList.findIndex(item => item === ascendingKey || item === descendingKey);

    if (this.isDeliveryLocationAscendingSortingActive()) {
        this.sortingList.update(list => list.filter((_, idx) => idx !== existingIndex));
    } else if (this.isDeliveryLocationDescendingSortingActive()) {
        this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? ascendingKey : item));
    } else {
        if (existingIndex > -1) {
            this.sortingList.update(list => list.map((item, idx) => idx === existingIndex ? descendingKey : item));
        } else {
            this.sortingList.update(list => [...list, descendingKey]);
        }
    }

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }

  getDisplayKeyForSort(key: string): string {
    const displayKeys: { [key: string]: string } = {
      parcelnumber: 'Parcel Number',
      pickupdate: 'Pick-up Date',
      requesterlocation: 'Requester Location',
      pickuplocation: 'Pick-up Location',
      deliverylocation: 'Delivery Location'
    };

    return displayKeys[key] || key;
  }

  removeSortingChips(sort: { key: string, order: 'asc' | 'desc' }) {
    this.resetOffsetAndOrderList();
    if (sort.key === 'Remove All Sortings') {
      this.removeAllSorting();
      this.updateTable();
      return;
    }

    const keyMapping: { [key: string]: string } = {
      'Parcel Number': 'parcelnumber',
      'Pick-up Date': 'pickupdate',
      'Requester Location': 'requesterlocation',
      'Pick-up Location': 'pickuplocation',
      'Delivery Location': 'deliverylocation',
    };

    const actualKey = keyMapping[sort.key] || sort.key;
    const formattedKey = sort.order === 'asc' ? actualKey : `-${actualKey}`;

    this.sortingList.update(list => list.filter(item => item !== formattedKey));

    this.saveSortingChipsToLocalStorage();
    this.updateTable();
  }

  removeAllSorting(resetCheckAllIntent: boolean = false) {
    this.resetOffsetAndOrderList(resetCheckAllIntent);
    this.sortingList.set([]);

    this.saveSortingChipsToLocalStorage();
  }

  onRemoveAllSortingsClick() {
    this.removeAllSorting();
    this.updateTable();
  }

  saveSortingChipsToLocalStorage() {
    const sortingState = {
      sortingList: this.sortingList()
    };

    localStorage.setItem('sortingChipsStateForMyPickups', JSON.stringify(sortingState));
  }

  loadSortingChipsFromLocalStorage() {
    const storedSortingState = localStorage.getItem('sortingChipsStateForMyPickups');

    if (storedSortingState) {
      const sortingState = JSON.parse(storedSortingState);

      if (sortingState.sortingList) {
        this.sortingList.set(sortingState.sortingList);
      }
    }
  }

  onResetToDefaultFiltersClick() {
    this.removeAllFilters();
    this.removeAllSorting(true);
    this.selectedRequestedPickupDateGt.set(this.getStandardDate(1));
    this.selectedRequestedPickupDateLte.set(this.getStandardDate(1));

    localStorage.removeItem('pickupDateRemoved');
    this.orderStatus.set('NEW');
    this.selectedStatuses.set(['NEW']);
    this.sortingList.set(["-pickupdate"]);

    this.checkAll.set(false);
    if (this.checkAllRef?.nativeElement) {
      this.checkAllRef.nativeElement.checked = false;
      this.checkAllRef.nativeElement.indeterminate = false;
    }

    this.setQueryParameters();
    this.saveFiltersToLocalStorage();
    this.saveSortingChipsToLocalStorage();
    this.saveCurrentOffsetToLocalStorage();
    this.updateTable();
  }

  sortParcelDetailsAscending(parcelDetails: any[]): any[] {
    if (!parcelDetails) return [];
    return [...parcelDetails].sort((a, b) => a.parcelKey.localeCompare(b.parcelKey));
  }

  sortParcelDetailsBySelectedStatus(parcelDetails: any[]): any[] {
     if (!this.selectedStatuses() || this.selectedStatuses().length === 0) {
      return this.sortParcelDetailsAscending(parcelDetails);
    }
    return parcelDetails.slice().sort((a, b) => {
      const aSelected = this.selectedStatuses().includes(a.operationalParcelStatus) ? 0 : 1;
      const bSelected = this.selectedStatuses().includes(b.operationalParcelStatus) ? 0 : 1;
      return aSelected - bSelected;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
